import { useState, useEffect } from "react";
import axios from "../../utils/axiosInstance";
import WeeklyReportTable from "./WeeklyReportTable";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/* =======================
   DATE HELPERS
======================= */

// Get ISO week number
function getWeekNumber(date) {
  const d = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
  );
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
}

// Get start & end of ISO week
function getStartEndOfWeek(week, year) {
  const simple = new Date(year, 0, 1 + (week - 1) * 7);
  const dow = simple.getDay();
  const ISOweekStart = simple;
  if (dow <= 4) ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1);
  else ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay());

  const start = new Date(ISOweekStart);
  const end = new Date(ISOweekStart);
  end.setDate(start.getDate() + 6);

  return { start, end };
}

// Get last 6 weeks
function getLastSixWeeks() {
  const result = [];
  const now = new Date();

  for (let i = 1; i <= 6; i++) {
    const d = new Date();
    d.setDate(now.getDate() - i * 7);

    const week = getWeekNumber(d);
    const year = d.getFullYear();
    const { start, end } = getStartEndOfWeek(week, year);

    const label = `Week ${week} (${start.toLocaleDateString()} → ${end.toLocaleDateString()})`;

    result.push({ label, week, year });
  }

  return result.reverse();
}

/* =======================
   COMPONENT
======================= */

const WeeklyPerGroup = ({ group }) => {
  const [devices, setDevices] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const lastSixWeeks = getLastSixWeeks();

  const [selectedWeek, setSelectedWeek] = useState(lastSixWeeks[5].week);
  const [selectedYear, setSelectedYear] = useState(lastSixWeeks[5].year);

  /* =======================
     FETCH DEVICES
  ======================= */
  useEffect(() => {
    if (!group?.id) return;

    const fetchDevices = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_GATEAWAY}/devices?groupId=${group.id}`,
        );
        setDevices(response.data || []);
      } catch (err) {
        console.error("Error fetching devices:", err);
      }
    };

    fetchDevices();
  }, [group?.id]);

  /* =======================
     FETCH WEEKLY REPORT
  ======================= */
  useEffect(() => {
    if (devices.length > 0) {
      fetchWeeklyReport(selectedWeek, selectedYear);
    }
  }, [devices, selectedWeek, selectedYear]);

  const fetchWeeklyReport = async (week, year) => {
    try {
      setLoading(true);
      setError(null);

      const payload = {
        year,
        week,
        units: devices.map((d) => d.id),
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/reports/precision-report-week`,
        payload,
        {
          headers: {
            id: "anything",
            "Content-Type": "application/json",
          },
        },
      );

      setReportData(response.data.data.mydict);
    } catch (err) {
      console.error(err);
      setError("Failed to load weekly report");
    } finally {
      setLoading(false);
    }
  };

  /* =======================
     DATA MAPPING
  ======================= */
  const unitMap = devices.map((u) => ({ id: u.id, name: u.name }));

  const getUnitName = (unitId) => {
    const unit = unitMap.find((u) => u.id === unitId);
    return unit ? unit.name : unitId;
  };

  const filteredReportData = reportData
    ? Object.entries(reportData).map(([unitId, report]) => ({
        ...report,
        unitId,
        unitName: getUnitName(unitId),
        weekLabel: `Week ${report.week} / ${report.year}`,
      }))
    : [];

  const filteredReportDataWithSearch = filteredReportData.filter((report) => {
    const query = searchQuery.toLowerCase();
    return (
      report.unitName.toLowerCase().includes(query) ||
      report.weekLabel.toLowerCase().includes(query) ||
      report.down.toString().includes(query) ||
      report.up.toString().includes(query) ||
      report.customer.toString().includes(query) ||
      report.precision.toString().includes(query) ||
      report.shutDowns.toString().includes(query)
    );
  });

  /* =======================
     EXPORTS (NO CHANGE)
  ======================= */
  const exportToCSV = () => {
    if (!filteredReportData.length) return;

    const csvContent = [
      [
        "Unit Name",
        "Year",
        "Week",
        "Stop(Hrs)",
        "Run(Hrs)",
        "Customer(Hrs)",
        "PRC(Hrs)",
        "Shutdowns",
      ],
      ...filteredReportData.map((r) => [
        r.unitName,
        r.year,
        r.week,
        r.down,
        r.up,
        r.customer,
        r.precision,
        r.shutDowns,
      ]),
    ];

    const csvString = csvContent
      .map((row) => row.map((f) => `"${f}"`).join(","))
      .join("\n");

    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Weekly_Report.csv`;
    link.click();
  };

  const exportToExcel = () => {
    if (!filteredReportData.length) return;

    const headers = [
      "Unit Name",
      "Year",
      "Week",
      "Stop(Hrs)",
      "Run(Hrs)",
      "Customer(Hrs)",
      "PRC(Hrs)",
      "Shutdowns",
    ];

    const rows = filteredReportData.map((r) => [
      r.unitName,
      r.year,
      r.week,
      r.down,
      r.up,
      r.customer,
      r.precision,
      r.shutDowns,
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Weekly Report");
    XLSX.writeFile(workbook, "Weekly_Report.xlsx");
  };

  const exportToPDF = () => {
    if (!filteredReportData.length) return;

    const doc = new jsPDF("l", "pt", "a4");
    doc.setFontSize(12);
    doc.text("Weekly Units Report", 40, 30);

    const headers = [
      [
        "Unit Name",
        "Year",
        "Week",
        "Stop(Hrs)",
        "Run(Hrs)",
        "Customer(Hrs)",
        "PRC(Hrs)",
        "Shutdowns",
      ],
    ];

    const rows = filteredReportData.map((r) => [
      r.unitName,
      r.year,
      r.week,
      r.down,
      r.up,
      r.customer,
      r.precision,
      r.shutDowns,
    ]);

    autoTable(doc, { head: headers, body: rows, startY: 50, theme: "grid" });
    doc.save("Weekly_Report.pdf");
  };

  /* =======================
     UI
  ======================= */
  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        <input
          type="text"
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="px-3 py-2 w-full sm:w-64 rounded-md border text-white border-gray-300 bg-transparent placeholder-gray-400 text-sm"
        />

        <div className="flex flex-wrap gap-2">
          <select
            value={`${selectedWeek}-${selectedYear}`}
            onChange={(e) => {
              const [week, year] = e.target.value.split("-").map(Number);
              setSelectedWeek(week);
              setSelectedYear(year);
            }}
            className="px-3 py-2 rounded-md border text-white border-gray-300 bg-slate-700 text-sm"
          >
            {lastSixWeeks.map((w) => (
              <option key={`${w.week}-${w.year}`} value={`${w.week}-${w.year}`}>
                {w.label}
              </option>
            ))}
          </select>

          <button
            onClick={exportToCSV}
            className="px-4 py-2 bg-blue-500 text-white rounded-md"
          >
            CSV
          </button>
          <button
            onClick={exportToExcel}
            className="px-4 py-2 bg-blue-500 text-white rounded-md"
          >
            Excel
          </button>
          <button
            onClick={exportToPDF}
            className="px-4 py-2 bg-blue-500 text-white rounded-md"
          >
            PDF
          </button>
        </div>
      </div>

      <WeeklyReportTable
        reportData={filteredReportDataWithSearch}
        units={devices}
        loading={loading}
        error={error}
      />
    </div>
  );
};

export default WeeklyPerGroup;
